import { fetchStaticJson } from './fetchStaticJson';
import { normalizeDirectUrlPath } from './getBotConfig';

export function buildBotConfigBucketUrl(domainName, botName) {
  const base = String(window.config?.baseApiUrl || '').replace(/\/$/, '');
  if (!base || !domainName || !botName) return '';
  return `${base}/bucketfiles/bots/${domainName}/${botName}_config.json`;
}

function getBotConfigSourceUrl(bot) {
  if (typeof bot === 'string') return bot.trim();
  if (bot && typeof bot === 'object' && typeof bot.configUrl === 'string') {
    return bot.configUrl.trim();
  }
  return '';
}

/** Strip hydrated configs so botsList.json only stores { configUrl, urlPath }. */
export function compactBotsListForPersist(botsList) {
  const source = botsList && typeof botsList === 'object' ? botsList : {};
  const next = {};
  Object.keys(source).forEach((key) => {
    if (key === 'defaultProject') {
      next.defaultProject = source.defaultProject;
      return;
    }
    const project = source[key];
    if (!project || typeof project !== 'object') return;
    const { bots, ...projectMeta } = project;
    const compactBots = {};
    if (bots && typeof bots === 'object') {
      Object.keys(bots).forEach((botName) => {
        const bot = bots[botName];
        const configUrl =
          getBotConfigSourceUrl(bot) || buildBotConfigBucketUrl(key, botName);
        if (!configUrl) return;
        const entry = { configUrl };
        const urlPath =
          bot &&
          typeof bot === 'object' &&
          typeof bot.urlPath === 'string' &&
          bot.urlPath.trim()
            ? normalizeDirectUrlPath(bot.urlPath)
            : '';
        if (urlPath) {
          entry.urlPath = urlPath;
        }
        compactBots[botName] = entry;
      });
    }
    next[key] = { ...projectMeta, bots: compactBots };
  });
  return next;
}

export async function fetchBotsListForRegistry() {
  const url = window.config?.botsListUrl;
  if (url) {
    try {
      const data = await fetchStaticJson(url);
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        return data;
      }
    } catch {
      // use in-memory copy
    }
  }
  return window.config?.botsList && typeof window.config.botsList === 'object'
    ? window.config.botsList
    : {};
}

export function setBotConfigUrlInBotsList(botsList, domainName, botName, options = {}) {
  const next = JSON.parse(JSON.stringify(botsList && typeof botsList === 'object' ? botsList : {}));
  const configUrl = buildBotConfigBucketUrl(domainName, botName);
  if (!domainName || !botName || !configUrl) {
    return { botsList: next, configUrl: '' };
  }
  if (!next[domainName] || typeof next[domainName] !== 'object') {
    next[domainName] = {
      defaultBot: botName,
      loadLastSession: true,
      bots: {},
    };
  }
  if (!next[domainName].bots || typeof next[domainName].bots !== 'object') {
    next[domainName].bots = {};
  }

  const existing = next[domainName].bots[botName];
  const existingUrlPath =
    existing &&
    typeof existing === 'object' &&
    typeof existing.urlPath === 'string' &&
    existing.urlPath.trim()
      ? normalizeDirectUrlPath(existing.urlPath)
      : '';
  const hasUrlPathOption = Object.prototype.hasOwnProperty.call(options, 'urlPath');
  const fromOptions = hasUrlPathOption
    ? normalizeDirectUrlPath(options.urlPath)
    : '';
  const urlPath = hasUrlPathOption ? fromOptions : existingUrlPath;

  const entry = { configUrl };
  if (urlPath) {
    entry.urlPath = urlPath;
  }
  next[domainName].bots[botName] = entry;
  if (!next[domainName].defaultBot) {
    next[domainName].defaultBot = botName;
  }
  return { botsList: next, configUrl };
}

/** Invalidate one bot in memory so the next ensure refetches it; keep other hydrated configs. */
export function patchBotConfigUrlInMemory(domainName, botName, options = {}) {
  window.config = window.config || {};
  const { botsList, configUrl } = setBotConfigUrlInBotsList(
    window.config.botsList,
    domainName,
    botName,
    options
  );
  window.config.botsList = botsList;
  return { botsList, configUrl };
}
